import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { ConciergeType } from 'src/lib/fliko/models/concierge/concierge.data';
import { DefectData } from 'src/lib/fliko/models/defects/defect.data';
import { IssueBase, IssueStatus } from 'src/lib/fliko/models/defects/issue.base';
import { DefectType } from 'src/lib/fliko/models/defects/defect-settings.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { SelectInputHelper } from 'src/app/shared/helpers/select-input.helper';
import { SelectInputData } from 'src/app/shared/interfaces/select-input-data.interface';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { DefectLocationCommon, DefectLocationType, DefectLocationVM } from '../service/models/defect-view-models';

export class DefectHelper {
  public static readonly STATUS_SELECT = [
    IssueStatus.NOTICED,
    IssueStatus.IN_PROGRESS,
    IssueStatus.FIXED,
    IssueStatus.CANCELLED,
    IssueStatus.ACCEPTED
  ];

  /**
   * Gets defects location regardless of defect format
   * @param {DefectData}          defect
   * @param {EstateType}          estateType
   * @param {LocalizationService} ls
   * @param {RentalUnit}          unit
   * @return {DefectLocationVM} DefectLocationVM
   */
  public static getLocation(
    defect: DefectData,
    estateType: EstateType,
    ls: LocalizationService,
    unit?: RentalUnit
  ): DefectLocationVM {
    const typeHR =
      defect.locationUnitId && defect.locationUnitType
        ? StrHlp.capitalize(GrammarHelper.unitTypeHR(defect.locationUnitType, estateType, ls))
        : ls.translate('defect.' + (defect.locationInside ? 'inside' : 'outside'));
    if (unit && unit.id == defect.locationUnitId) {
      return {
        block: DefectHelper.sanitizeLocation(unit.blockNumber),
        staircase: DefectHelper.sanitizeLocation((unit as ApartmentData).staircaseNumber),
        floor: DefectHelper.sanitizeLocation(unit.floor),
        number: DefectHelper.sanitizeLocation(unit.number),
        typeHR
      };
    } else {
      // ! Set by mobile, removed from model - failsafe if apt object not found
      const legacyNumber = (defect as any).locationApartment;
      return {
        block: DefectHelper.sanitizeLocation(defect.locationBuilding),
        staircase: DefectHelper.sanitizeLocation(defect.locationStairway),
        floor: DefectHelper.sanitizeLocation(defect.locationFloor),
        number: legacyNumber ? DefectHelper.sanitizeLocation(legacyNumber) : null,
        typeHR
      };
    }
  }

  /**
   * Returns translated location type
   * @param {DefectLocationType | string} type
   * @param {EstateType}                  estateType
   * @param {LocalizationService}         ls
   * @param {string}                      placeholder
   * @return {string}
   */
  public static getLocationTypeHR(
    type: DefectLocationType | string,
    estateType: EstateType,
    ls: LocalizationService,
    placeholder: string = ''
  ): string {
    const lh = new LocalizationHelper(ls);
    switch (type) {
      case DefectLocationCommon.BUILDING:
      case DefectLocationCommon.OUTDOOR:
        return lh.locationCommonHR(type);
      case RentalUnitType.APARTMENT:
      case RentalUnitType.PARKING:
      case RentalUnitType.STORAGE:
        return GrammarHelper.unitTypeHR(type, estateType, ls);
      default:
        return placeholder;
    }
  }

  /**
   * Gets defects location in short string format
   * @param {DefectLocationVM} vm
   * @param {boolean}          withType should (inside?) parameter be added
   * @return {string} location string in "block | staircase | floor | number | (inside?)" pattern
   */
  public static getLocationShort(vm: DefectLocationVM, withType = true): string {
    const address = [vm.block, vm.staircase, vm.floor, vm.number].map((x) =>
      ToString.or(x == DefectLocationVM.ALL ? '*' : x?.toUpperCase(), '-')
    );

    while (address[address.length - 1] == '-') {
      address.pop();
    }

    if (withType) address.push(vm.typeHR);

    return address.join(' | ');
  }

  /**
   * Use to build unambiguous location component key
   * @param value bd location component
   * @return trimmed, no-empty, lower case string || null
   */
  public static sanitizeLocation(value: string | null | undefined): string | null {
    return value?.trim().toLocaleLowerCase() || null;
  }

  /**
   * Builds SelectInputData from DefectTypes
   * @param {DefectType}          types
   * @param {LocalizationService} ls
   * @return {SelectInputData<string>[]}
   */
  public static buildTypeSelect(types: (DefectType | ConciergeType)[], ls: LocalizationService): SelectInputData<string>[] {
    const lh = new LocalizationHelper(ls);
    return SelectInputHelper.fromObjects(types, (x) => lh.defectTypeLabel(x));
  }

  /**
   * Converts defect's refId to integer
   * @param {DefectData} defect
   * @return {number} -1 if no refIf
   */
  public static getRefIdNumber(defect: IssueBase): number {
    return parseInt(defect.refId?.match(/(\d+)/g)?.join('') || '-1');
  }
}
